import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress,
  Alert, Divider, useTheme, Collapse, IconButton,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Flag as HITLIcon,
  Hub as SubnetIcon,
} from '@mui/icons-material';
import { RootState } from '../../types';
import { fetchClientPolicy, upsertClientPolicy } from '../../store/slices/clientsSlice';
import { PlaybooksService } from '../../services/playbooksService';

const SEVERITIES = [
  { value: 'low', label: 'Low', color: '#22c55e' },
  { value: 'medium', label: 'Medium', color: '#eab308' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'critical', label: 'Critical', color: '#ef4444' },
];

interface Props {
  clientId: string;
}

const ClientPolicyPanel: React.FC<Props> = ({ clientId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { policy, policyLoading } = useSelector((state: RootState) => state.clients);

  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const [hitlMinConfidence, setHitlMinConfidence] = useState<string>('');
  const [hitlMaxCriticality, setHitlMaxCriticality] = useState<string>('');
  const [hitlMaxNoise, setHitlMaxNoise] = useState<string>('');
  const [hitlSaving, setHitlSaving] = useState(false);
  const [hitlSaved, setHitlSaved] = useState(false);
  const [hitlError, setHitlError] = useState<string | null>(null);

  const [subnetEnabled, setSubnetEnabled] = useState(false);
  const [subnetPrefixLength, setSubnetPrefixLength] = useState('24');
  const [subnetSaving, setSubnetSaving] = useState(false);
  const [subnetSaved, setSubnetSaved] = useState(false);
  const [subnetError, setSubnetError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchClientPolicy(clientId) as any);
  }, [clientId, dispatch]);

  useEffect(() => {
    if (policy) {
      setSelectedSeverities(policy.auto_ticket_severities || []);
      const hitl = (policy as any).settings?.hitl_criteria ?? {};
      setHitlMinConfidence(hitl.min_confidence ?? '');
      setHitlMaxCriticality(hitl.max_asset_criticality ?? '');
      setHitlMaxNoise(hitl.max_noise_warnings != null ? String(hitl.max_noise_warnings) : '');

      const subnet = (policy as any).settings?.subnet_correlation ?? {};
      setSubnetEnabled(!!subnet.enabled);
      setSubnetPrefixLength(subnet.prefix_length != null ? String(subnet.prefix_length) : '24');
    }
  }, [policy]);

  const toggleSeverity = (sev: string) => {
    setSelectedSeverities((prev) =>
      prev.includes(sev) ? prev.filter((s) => s !== sev) : [...prev, sev]
    );
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    const result = await dispatch(upsertClientPolicy({
      clientId,
      policy: {
        auto_ticket_severities: selectedSeverities,
        custom_rules: policy?.custom_rules || [],
      },
    }) as any);

    if (result.error) {
      setSaveError(result.payload || 'Failed to save policy');
    } else {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const handleSaveHITL = async () => {
    setHitlSaving(true);
    setHitlError(null);
    setHitlSaved(false);
    try {
      await PlaybooksService.setHITLCriteria(clientId, {
        min_confidence: (hitlMinConfidence as any) || null,
        max_asset_criticality: (hitlMaxCriticality as any) || null,
        max_noise_warnings: hitlMaxNoise !== '' ? parseInt(hitlMaxNoise, 10) : null,
      });
      setHitlSaved(true);
      setTimeout(() => setHitlSaved(false), 3000);
    } catch (e: any) {
      setHitlError(e.message);
    } finally {
      setHitlSaving(false);
    }
  };

  const handleSaveSubnetCorrelation = async () => {
    setSubnetSaving(true);
    setSubnetError(null);
    setSubnetSaved(false);
    try {
      await PlaybooksService.setSubnetCorrelationSettings(clientId, {
        enabled: subnetEnabled,
        prefix_length: parseInt(subnetPrefixLength, 10),
      });
      setSubnetSaved(true);
      setTimeout(() => setSubnetSaved(false), 3000);
    } catch (e: any) {
      setSubnetError(e.message);
    } finally {
      setSubnetSaving(false);
    }
  };

  const labelSx = {
    color: theme.palette.text.tertiary,
    fontWeight: 700,
    display: 'block',
    mb: 1,
    letterSpacing: '0.05em',
    fontSize: '0.7rem',
  };

  return (
    <Box>
      {/* Auto-ticket policy */}
      <Paper sx={{ p: 3, mb: 2, backgroundColor: theme.palette.background.secondary }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Auto-Ticket Policy
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
          Alerts at the selected severity levels will automatically become tickets when ingested
          by the pipeline — no analyst action required.
        </Typography>

        <Typography variant="caption" sx={labelSx}>
          AUTO-TICKET WHEN SEVERITY IS
        </Typography>

        {policyLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {SEVERITIES.map(({ value, label, color }) => {
              const active = selectedSeverities.includes(value);
              return (
                <Chip
                  key={value}
                  label={label}
                  onClick={() => toggleSeverity(value)}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    backgroundColor: active ? `${color}22` : theme.palette.background.tertiary,
                    color: active ? color : theme.palette.text.secondary,
                    border: `1.5px solid ${active ? color : 'transparent'}`,
                    '&:hover': {
                      backgroundColor: `${color}33`,
                      color: color,
                      border: `1.5px solid ${color}`,
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        {selectedSeverities.length === 0 && (
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', mb: 2 }}>
            No severities selected — auto-ticket creation is disabled.
          </Typography>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>Policy saved.</Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
        )}

        <Button
          variant="contained"
          startIcon={policyLoading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={policyLoading}
          size="small"
        >
          Save Policy
        </Button>
      </Paper>

      {/* AI Handoff Criteria (HITL) */}
      <Paper sx={{ p: 3, mb: 2, backgroundColor: theme.palette.background.secondary }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <HITLIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>AI Handoff Criteria</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
          Sessions matching any of these conditions are flagged for human review (<code>requires_human_review = true</code>).
          Leave a field blank to disable that gate.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Min confidence to flag</InputLabel>
            <Select
              value={hitlMinConfidence}
              label="Min confidence to flag"
              onChange={(e) => setHitlMinConfidence(e.target.value)}
              sx={{ fontSize: '0.82rem', backgroundColor: theme.palette.background.tertiary }}
            >
              <MenuItem value=""><em>Disabled</em></MenuItem>
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Max asset criticality to flag</InputLabel>
            <Select
              value={hitlMaxCriticality}
              label="Max asset criticality to flag"
              onChange={(e) => setHitlMaxCriticality(e.target.value)}
              sx={{ fontSize: '0.82rem', backgroundColor: theme.palette.background.tertiary }}
            >
              <MenuItem value=""><em>Disabled</em></MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Max noise warnings</InputLabel>
            <Select
              value={hitlMaxNoise}
              label="Max noise warnings"
              onChange={(e) => setHitlMaxNoise(e.target.value)}
              sx={{ fontSize: '0.82rem', backgroundColor: theme.palette.background.tertiary }}
            >
              <MenuItem value=""><em>Disabled</em></MenuItem>
              {[0, 1, 2, 3, 5].map((n) => (
                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {hitlSaved && <Alert severity="success" sx={{ mb: 2 }}>HITL criteria saved.</Alert>}
        {hitlError && <Alert severity="error" sx={{ mb: 2 }}>{hitlError}</Alert>}

        <Button
          variant="outlined"
          size="small"
          startIcon={hitlSaving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveHITL}
          disabled={hitlSaving}
        >
          {hitlSaving ? 'Saving…' : 'Save HITL criteria'}
        </Button>
      </Paper>

      {/* Subnet-aware correlation */}
      <Paper sx={{ p: 3, mb: 2, backgroundColor: theme.palette.background.secondary }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SubnetIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Subnet-Aware Correlation</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          When enabled, alerts with different exact IPs in the same subnet (e.g. reused attacker
          infrastructure) can correlate into one ticket. This is a fuzzier signal than exact IOC
          matching — off by default, and a false merge can be undone per-alert from the
          correlation detail view.
        </Typography>

        <FormControlLabel
          control={<Switch checked={subnetEnabled} onChange={(e) => { setSubnetEnabled(e.target.checked); setSubnetSaved(false); }} />}
          label={<Typography variant="body2">Enable subnet correlation</Typography>}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={!subnetEnabled}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Subnet size</InputLabel>
            <Select
              value={subnetPrefixLength}
              label="Subnet size"
              onChange={(e) => { setSubnetPrefixLength(e.target.value); setSubnetSaved(false); }}
              sx={{ fontSize: '0.82rem', backgroundColor: theme.palette.background.tertiary }}
            >
              {[16, 20, 22, 24, 26, 28, 30].map((n) => (
                <MenuItem key={n} value={String(n)}>/{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {subnetSaved && <Alert severity="success" sx={{ mb: 2 }}>Subnet correlation settings saved.</Alert>}
        {subnetError && <Alert severity="error" sx={{ mb: 2 }}>{subnetError}</Alert>}

        <Button
          variant="outlined"
          size="small"
          startIcon={subnetSaving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveSubnetCorrelation}
          disabled={subnetSaving}
        >
          {subnetSaving ? 'Saving…' : 'Save subnet correlation settings'}
        </Button>
      </Paper>

      {/* Custom rules (read-only preview) */}
      {policy?.custom_rules && policy.custom_rules.length > 0 && (
        <Paper sx={{ p: 2, backgroundColor: theme.palette.background.secondary }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setRulesOpen((o) => !o)}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Custom Rules ({policy.custom_rules.length})
            </Typography>
            <IconButton size="small">
              {rulesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={rulesOpen}>
            <Divider sx={{ my: 1.5 }} />
            <Box
              component="pre"
              sx={{
                fontSize: '0.72rem',
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.background.default,
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 300,
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              {JSON.stringify(policy.custom_rules, null, 2)}
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default ClientPolicyPanel;
